import { useCallback, useRef } from 'react';

const OFFSET_PROPERTY = '--sticky-header-offset';
const ENABLED_ATTRIBUTE = 'data-sticky-header';
const STUCK_ATTRIBUTE = 'data-sticky-header-stuck';

const attachStickyHeader = (table: HTMLTableElement) => {
  let frame: number | undefined;
  let lastOffset: number | undefined;

  const update = () => {
    frame = undefined;
    const header = table.tHead;
    if (!header) {
      return;
    }

    const { top, height } = table.getBoundingClientRect();
    const maxOffset = Math.max(0, height - header.offsetHeight);
    const offset = Math.min(Math.max(0, -top), maxOffset);
    if (offset === lastOffset && header.hasAttribute(ENABLED_ATTRIBUTE)) {
      return;
    }
    lastOffset = offset;

    header.setAttribute(ENABLED_ATTRIBUTE, '');
    header.style.setProperty(OFFSET_PROPERTY, `${offset}px`);
    header.toggleAttribute(STUCK_ATTRIBUTE, offset > 0);
  };

  const scheduleUpdate = () => {
    if (frame === undefined) {
      frame = window.requestAnimationFrame(update);
    }
  };

  update();
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);
  // rows are added by infinite scrolling / filtering, which changes how far
  // the header is allowed to travel
  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleUpdate)
      : undefined;
  resizeObserver?.observe(table);

  return () => {
    if (frame !== undefined) {
      window.cancelAnimationFrame(frame);
    }
    window.removeEventListener('scroll', scheduleUpdate);
    window.removeEventListener('resize', scheduleUpdate);
    resizeObserver?.disconnect();

    const header = table.tHead;
    header?.removeAttribute(ENABLED_ATTRIBUTE);
    header?.removeAttribute(STUCK_ATTRIBUTE);
    header?.style.removeProperty(OFFSET_PROPERTY);
  };
};

// Keeps a table's header row visible while the page scrolls past it.
//
// position: sticky can't be used here because our tables live inside
// horizontally scrollable wrappers (overflow-x: auto), which become the
// sticky scroll container and never scroll vertically. Instead, the header
// cells are translated down by how far the table has scrolled above the
// viewport, which also keeps them aligned with their columns when the table
// is scrolled horizontally. See the matching styles in _custom.scss.
//
// Returns a callback ref to attach to the <table> element.
export const useStickyTableHeader = (enabled: boolean) => {
  const detachRef = useRef<(() => void) | undefined>(undefined);

  // React calls the previous callback with null before calling a new one
  // (e.g. when `enabled` changes or the table remounts), so every attach is
  // preceded by a detach
  return useCallback(
    (table: HTMLTableElement | null) => {
      detachRef.current?.();
      detachRef.current =
        table && enabled ? attachStickyHeader(table) : undefined;
    },
    [enabled]
  );
};
